import { DeleteOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import React from 'react';
import { Props } from '.';
import CapSelector from '../../../../components/cap-selector/CapSelector';
import Icon from '../../../../components/icon/Icon';
import { CapNumber } from '../../../../core/models/CapNumber';
import { getFieldPlayIneligibleCapNumbers, getTeamId } from '../../../../helpers/EventHelper';
import {
    CreateMatchPlayerCommandOfMatchPlayer,
    EventVm,
    TurnoverRequest,
    TurnoverTypeEnum,
    UpdateEventCommand,
} from '../../../../utils/api';

class Turnover extends React.PureComponent<Props> {
    public handleStolenBySelect = (cap: number): void => {
        const { event, updateEvent, lineups } = this.props;

        const eventVm = event as EventVm;

        const lineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean =>
                l.teamId !== getTeamId(event) && l.number === cap
        );

        const updatedEvent = new UpdateEventCommand({
            ...event,
            turnover: new TurnoverRequest({
                ...event.turnover,
                wonPossesionPlayerId: lineup?.id,
                id: eventVm.turnoverId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleStolenByClear = (): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            turnover: new TurnoverRequest({
                ...event.turnover,
                wonPossesionPlayerId: undefined,
                id: eventVm.turnoverId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleTurnoverTypeSelect = (turnoverType: TurnoverTypeEnum): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            turnover: new TurnoverRequest({
                ...event.turnover,
                typeId: turnoverType,
                wonPossesionPlayerId: undefined,
                id: eventVm.turnoverId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public render = (): React.ReactElement => {
        const { event, lineups, isHomeTeam, clearEvent, deleteEvent, events } = this.props;
        const eventVm = event as EventVm;
        const eventExists = !!eventVm.id;

        const turnoverType = event.turnover?.typeId;
        const stolenById = event.turnover?.wonPossesionPlayerId;
        const stolenByLineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === stolenById
        );
        const otherTeamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId !== getTeamId(event)
        );
        const stolenByDisabledCaps = getFieldPlayIneligibleCapNumbers(events, otherTeamLineups);
        let title = 'Stolen by';
        switch (turnoverType) {
            case TurnoverTypeEnum.Offensive_Foul:
                title = 'Fouled Player';
                break;
            case TurnoverTypeEnum.Ball_Under:
                title = 'Submerged by';
                break;
            default:
                title = 'Stolen by';
        }

        const showCaps =
            turnoverType === TurnoverTypeEnum.Lost_Ball ||
            turnoverType === TurnoverTypeEnum.Ball_Under ||
            turnoverType === TurnoverTypeEnum.Offensive_Foul;

        const pill = (label: string, active: boolean, onClick: () => void): React.ReactElement => (
            <span
                className={`c-shot-pill${active ? ' c-shot-pill--active' : ''}`}
                onClick={onClick}
                role="button"
            >
                {label}
            </span>
        );

        return (
            <div className="event-create">
                <div className="c-shot-form__grid">
                    <div className="c-shot-form__col">
                        {pill('Lost Ball', turnoverType === TurnoverTypeEnum.Lost_Ball, () =>
                            this.handleTurnoverTypeSelect(TurnoverTypeEnum.Lost_Ball)
                        )}
                        {pill('Off. Foul', turnoverType === TurnoverTypeEnum.Offensive_Foul, () =>
                            this.handleTurnoverTypeSelect(TurnoverTypeEnum.Offensive_Foul)
                        )}
                    </div>
                    <div className="c-shot-form__col">
                        {pill('Ball Under', turnoverType === TurnoverTypeEnum.Ball_Under, () =>
                            this.handleTurnoverTypeSelect(TurnoverTypeEnum.Ball_Under)
                        )}
                        {pill('Clock Exp.', turnoverType === TurnoverTypeEnum.Clock_Expired, () =>
                            this.handleTurnoverTypeSelect(TurnoverTypeEnum.Clock_Expired)
                        )}
                    </div>
                    {showCaps && (
                        <div className="c-shot-form__caps">
                            <div className="c-shot-form__caps-label">{title}</div>
                            <CapSelector
                                capClass={`c-shot-form__cap-selector c-shot-form__cap-selector--${
                                    isHomeTeam ? 'away' : 'home'
                                }`}
                                onSelect={this.handleStolenBySelect}
                                onClear={this.handleStolenByClear}
                                caps={otherTeamLineups.map(
                                    (l: CreateMatchPlayerCommandOfMatchPlayer): CapNumber => ({
                                        cap: l.number,
                                        capNumber: l.capNumber || l.number,
                                    })
                                )}
                                activeCap={stolenByLineup ? stolenByLineup.number : null}
                                activeCapNumber={
                                    stolenByLineup
                                        ? stolenByLineup.capNumber || stolenByLineup.number
                                        : null
                                }
                                disabledCaps={stolenByDisabledCaps}
                            />
                        </div>
                    )}
                    <div className="c-shot-form__actions">
                        <span
                            className="c-shot-form__action-btn"
                            onClick={(): void => clearEvent()}
                            role="button"
                            title="Close"
                        >
                            <Icon name="icon-times" />
                        </span>
                        {eventExists && (
                            <Popconfirm
                                title="Are you sure you want to delete this event?"
                                onConfirm={(): void => deleteEvent(eventVm)}
                                okText="Delete"
                                okButtonProps={{ danger: true }}
                                cancelText="Cancel"
                            >
                                <span
                                    className="c-shot-form__action-btn c-shot-form__action-btn--delete"
                                    role="button"
                                    title="Delete"
                                >
                                    <DeleteOutlined />
                                </span>
                            </Popconfirm>
                        )}
                    </div>
                </div>
            </div>
        );
    };
}

export default Turnover;
